import { useCallback, useMemo, useState } from "react";

import {
  useGetBillingConfig,
  useGetBillingInvoices,
  useGetBillingShops,
  useGetBillingSummary,
  useGetCommissions,
  usePayCommissionDebt,
} from "@/api";
import type { BillingSummaryDataDto } from "@/api/schemas/billingSummaryDataDto";
import type {
  BillingCommissionView,
  BillingInvoiceView,
} from "@/components/Templates/Billing/Billing.template.types";
import { useCommission } from "@/hooks/useCommission";
import { formatCommissionRate, toAmount } from "@/lib/billing/constants";
import { formatPersianDateMonthDay, formatPersianNumber } from "@/lib/format/persian";
import { notifyError } from "@/stores/notification.store";

export type {
  BillingCommissionView,
  BillingInvoiceView,
} from "@/components/Templates/Billing/Billing.template.types";

const COMMISSIONS_PAGE_SIZE = 20;

export function useBillingPage() {
  const { canPayDebt } = useCommission();

  const { data: configData } = useGetBillingConfig({ query: { staleTime: Infinity } });
  const { data: summaryData, isLoading: isSummaryLoading } = useGetBillingSummary();
  const { data: shopsData, isLoading: isShopsLoading } = useGetBillingShops();
  const { data: commissionsData, isLoading: isCommissionsLoading } = useGetCommissions({
    per_page: COMMISSIONS_PAGE_SIZE,
  });
  const { data: invoicesData, isLoading: isInvoicesLoading } = useGetBillingInvoices();

  const config = configData?.status === 200 ? configData.data.data : undefined;
  const summary: BillingSummaryDataDto | undefined =
    summaryData?.status === 200 ? summaryData.data.data : undefined;
  const shops = useMemo(() => (shopsData?.status === 200 ? shopsData.data.data : []), [shopsData]);

  const shopNameById = useMemo(
    () => Object.fromEntries(shops.map((shop) => [shop.store_id, shop.store_name] as const)),
    [shops],
  );

  const commissions: BillingCommissionView[] = useMemo(() => {
    const rows = commissionsData?.status === 200 ? commissionsData.data.data : [];
    return rows.map((commission) => ({
      id: commission.id,
      shopName: shopNameById[commission.store_id] ?? "",
      orderNumber: commission.order_number ? formatPersianNumber(commission.order_number) : undefined,
      date: formatPersianDateMonthDay(new Date(commission.created_at)),
      orderAmount: formatPersianNumber(toAmount(commission.order_amount)),
      amount: formatPersianNumber(toAmount(commission.amount)),
      settlement: commission.settlement,
      status: commission.status,
    }));
  }, [commissionsData, shopNameById]);

  const invoices: BillingInvoiceView[] = useMemo(() => {
    const rows = invoicesData?.status === 200 ? invoicesData.data.data : [];
    return rows.map((invoice) => ({
      id: invoice.id,
      shopName: shopNameById[invoice.store_id] ?? "",
      date: formatPersianDateMonthDay(new Date(invoice.paid_at ?? invoice.created_at)),
      amount: formatPersianNumber(toAmount(invoice.amount)),
      status: invoice.status,
    }));
  }, [invoicesData, shopNameById]);

  const [payingStoreId, setPayingStoreId] = useState<number | null>(null);

  const { mutate: payDebt, isPending: isPayPending } = usePayCommissionDebt({
    mutation: {
      onSuccess: (response) => {
        setPayingStoreId(null);
        if (response.status !== 200) return;
        const redirectUrl = response.data.data.redirect_url;
        if (redirectUrl) window.location.href = redirectUrl;
      },
      onError: (error) => {
        setPayingStoreId(null);
        notifyError(error, "اتصال به درگاه پرداخت انجام نشد؛ دوباره تلاش کنید");
      },
    },
  });

  const onPayDebt = useCallback(
    (storeId: number) => {
      setPayingStoreId(storeId);
      payDebt({ storeId });
    },
    [payDebt],
  );

  const debtLimit = toAmount(config?.debt_limit ?? summary?.debt_limit);
  const isLoading = isSummaryLoading || isShopsLoading || isCommissionsLoading || isInvoicesLoading;

  return {
    summary,
    shops,
    commissions,
    invoices,
    rateLabel: formatCommissionRate(toAmount(config?.commission_rate ?? 0.04)),
    debtLimitLabel: formatPersianNumber(debtLimit),
    debtLimit,
    isLoading,
    isSummaryLoading,
    isEmpty: !isLoading && shops.length === 0,
    isPayPending,
    payingStoreId,
    onPayDebt,
    canPayDebt,
  };
}
